/**
 * AI Assistant Library
 * 
 * Comprehensive AI system with:
 * - Full codebase knowledge
 * - Internet search capability
 * - Customizable prompts and behaviors
 * - Context management
 * - Claude API integration
 */

import { projectId } from '/utils/supabase/info';
import { getAuthHeadersForProtectedApi } from './db-storage';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface AIMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  metadata?: {
    tokens?: number;
    model?: string;
    searchResults?: any[];
    codeContext?: string[];
    liveCodeActions?: any[];
  };
}

export interface AIConversation {
  id: string;
  title: string;
  messages: AIMessage[];
  createdAt: string;
  updatedAt: string;
  userId?: string;
  tags?: string[];
}

export interface AIConfig {
  model: 'llama-3.3-70b-versatile' | 'llama-3.1-8b-instant' | 'mixtral-8x7b-32768' | 'gemma2-9b-it';
  temperature: number;
  maxTokens: number;
  systemPrompt: string;
  enableCodebaseContext: boolean;
  enableWebSearch: boolean;
  enableMemory: boolean;
  customInstructions: string;
  contextWindow: number;
  responseStyle: 'concise' | 'detailed' | 'technical' | 'friendly';
}

export interface CodebaseIndex {
  files: {
    path: string;
    type: 'component' | 'page' | 'lib' | 'config' | 'style';
    description: string;
    exports: string[];
    dependencies: string[];
  }[];
  structure: {
    pages: string[];
    components: string[];
    utilities: string[];
    api: string[];
  };
  features: string[];
  techStack: string[];
}

// ============================================================================
// CODEBASE INDEX (Auto-generated knowledge base)
// ============================================================================

export const CODEBASE_INDEX: CodebaseIndex = {
  files: [
    {
      path: '/src/app/App.tsx',
      type: 'component',
      description: 'Main app component with RouterProvider',
      exports: ['App'],
      dependencies: ['react-router', 'AuthProvider'],
    },
    {
      path: '/src/app/pages/Home.tsx',
      type: 'page',
      description: 'Dashboard with RTI score, action cards, and timeline',
      exports: ['Home'],
      dependencies: ['RTIScore', 'ActionCard', 'Timeline'],
    },
    {
      path: '/src/app/pages/DailyCheckIn.tsx',
      type: 'page',
      description: '5-question daily check-in for RTI calculation',
      exports: ['DailyCheckIn'],
      dependencies: ['RTICalculator'],
    },
    {
      path: '/src/app/lib/rti-calculator.tsx',
      type: 'lib',
      description: 'RTI scoring algorithm - 5 components weighted calculation',
      exports: ['RTICalculator', 'calculateRTI'],
      dependencies: [],
    },
    {
      path: '/src/app/lib/marketing.tsx',
      type: 'lib',
      description: 'Marketing automation, social media, email campaigns',
      exports: ['ContentGenerator', 'MarketingAPI'],
      dependencies: [],
    },
    {
      path: '/src/app/lib/seo.tsx',
      type: 'lib',
      description: 'SEO utilities - meta tags, structured data, schemas',
      exports: ['useSEO', 'useStructuredData', 'SEOPresets'],
      dependencies: [],
    },
    {
      path: '/src/app/pages/Marketing.tsx',
      type: 'page',
      description: 'Marketing dashboard with social composer, campaigns, analytics',
      exports: ['Marketing'],
      dependencies: ['EmailCampaignManager', 'SocialMediaManager'],
    },
    {
      path: '/src/app/pages/Blog.tsx',
      type: 'page',
      description: 'SEO-optimized blog hub with articles and search',
      exports: ['Blog'],
      dependencies: ['useSEO'],
    },
    {
      path: '/src/app/pages/Resources.tsx',
      type: 'page',
      description: 'Free tools hub - quizzes, calculators, templates',
      exports: ['Resources'],
      dependencies: ['useSEO'],
    },
  ],
  structure: {
    pages: [
      'Home', 'DailyCheckIn', 'Timeline', 'Settings', 'Pricing',
      'Blog', 'Resources', 'Marketing', 'Admin', 'Auth',
    ],
    components: [
      'RTIScore', 'ActionCard', 'Timeline', 'SubscriptionManager',
      'EmailCampaignManager', 'SocialMediaManager',
    ],
    utilities: [
      'rti-calculator', 'marketing', 'seo', 'ai-assistant',
      'storage', 'auth', 'analytics',
    ],
    api: [
      '/api/check-in', '/api/action-cards', '/api/subscription',
      '/api/marketing', '/api/ai-assistant',
    ],
  },
  features: [
    'RTI Scoring System (5 components)',
    'Daily Check-ins',
    'Action Cards (Restraint, Reconnection, Stability)',
    'Timeline Visualization',
    '72-hour Forecasting',
    'Singles Mode',
    'Couples Mode',
    'Push Notifications',
    'Offline Support',
    'Marketing Automation',
    'SEO Optimization',
    'Blog & Resources',
    'Subscription Management',
    'Beta System',
  ],
  techStack: [
    'React 18.3.1',
    'TypeScript',
    'Vite',
    'Tailwind CSS',
    'Shadcn/UI',
    'React Router',
    'Supabase',
    'Vercel',
  ],
};

// ============================================================================
// DEFAULT AI CONFIGURATION
// ============================================================================

export const DEFAULT_AI_CONFIG: AIConfig = {
  model: 'llama-3.3-70b-versatile',
  temperature: 0.7,
  maxTokens: 4096,
  systemPrompt: `You are TENSION AI, an expert assistant for the TENSION relationship intelligence app. You have complete knowledge of:

**App Purpose:**
TENSION measures, forecasts, and stabilizes relationship tension using a 5-component RTI (Relationship Tension Index) scoring system with daily check-ins, personalized action cards, and timeline visualization.

**Core Features:**
- RTI Scoring: 5 behavioral components (Initiation, Response Latency, Emotional Density, Over-availability, Withdrawal)
- Daily Check-ins: 5 questions to track relationship health
- Action Cards: Restraint, Reconnection, or Stability guidance
- Timeline: Visualize relationship trends over time
- Forecasting: 72-hour tension predictions
- Dual Modes: Singles Mode + Couples Mode

**Tech Stack:**
React 18, TypeScript, Vite, Tailwind CSS, Shadcn/UI, React Router, Supabase, Vercel

**Your Capabilities:**
1. Answer questions about TENSION features, code, and architecture
2. Help debug issues and suggest fixes
3. Generate code snippets and components
4. Explain RTI calculations and relationship psychology
5. Assist with marketing, SEO, and growth strategies
6. Search the internet for relevant resources when needed
7. **UPDATE THE SITE IN REAL-TIME** - You can create/modify components, pages, and utilities instantly!

**🔥 LIVE CODE SYSTEM:**
You can now update the TENSION site in REAL-TIME without GitHub or Vercel deployment!

**Available Commands:**
- CREATE_COMPONENT: Create a new React component
- UPDATE_PAGE: Modify an existing page
- CREATE_PAGE: Create a brand new page
- UPDATE_UTILITY: Modify utility functions
- CREATE_UTILITY: Create new utility library

**How to Use:**
When the user asks you to create or update something, respond with:

\`\`\`LIVE_CODE_ACTION
{
  "action": "CREATE_COMPONENT",
  "path": "/src/app/components/NewFeature.tsx",
  "type": "component",
  "name": "NewFeature",
  "description": "What this does",
  "code": "... full TypeScript/React code here ..."
}
\`\`\`

**Important Rules:**
1. Always provide complete, working code (no placeholders)
2. Use TypeScript with proper types
3. Follow React 18 best practices
4. Match existing TENSION code style
5. Include all necessary imports
6. Export default for pages/components
7. Test code mentally before providing

**Example - Create Component:**
\`\`\`LIVE_CODE_ACTION
{
  "action": "CREATE_COMPONENT",
  "path": "/src/app/components/StatsWidget.tsx",
  "type": "component",
  "name": "StatsWidget",
  "description": "Display key statistics",
  "code": "import { FC } from 'react';\\nimport { Card } from './ui/card';\\n\\ninterface StatsWidgetProps {\\n  title: string;\\n  value: number;\\n}\\n\\nexport const StatsWidget: FC<StatsWidgetProps> = ({ title, value }) => {\\n  return (\\n    <Card className=\\"p-4\\">\\n      <h3 className=\\"text-sm text-neutral-600\\">{title}</h3>\\n      <p className=\\"text-2xl font-bold\\">{value}</p>\\n    </Card>\\n  );\\n};\\n\\nexport default StatsWidget;"
}
\`\`\`

Changes take effect **immediately** - no deployment needed!

**Tone:**
Be helpful, technical when needed, and empathetic. You understand both the technical side and the emotional/relationship context of this app.`,
  enableCodebaseContext: true,
  enableWebSearch: true,
  enableMemory: true,
  customInstructions: '',
  contextWindow: 10,
  responseStyle: 'detailed',
};

// ============================================================================
// AI ASSISTANT CLASS
// ============================================================================

export class AIAssistant {
  private static config: AIConfig = DEFAULT_AI_CONFIG;
  private static conversationHistory: AIMessage[] = [];
  
  /**
   * Initialize AI Assistant with configuration
   */
  static initialize(config?: Partial<AIConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }
  }

  /**
   * Get current configuration
   */
  static getConfig(): AIConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  static updateConfig(updates: Partial<AIConfig>) {
    this.config = { ...this.config, ...updates };
    // Save to localStorage
    localStorage.setItem('tension_ai_config', JSON.stringify(this.config));
  }

  /**
   * Load configuration from localStorage
   */
  static loadConfig() {
    const saved = localStorage.getItem('tension_ai_config');
    if (saved) {
      try {
        this.config = { ...DEFAULT_AI_CONFIG, ...JSON.parse(saved) };
      } catch (error) {
        console.error('Failed to load AI config:', error);
      }
    }
  }

  /**
   * Send message to AI with full context
   */
  static async sendMessage(
    message: string,
    options?: {
      conversationId?: string;
      includeCodeContext?: boolean;
      includeWebSearch?: boolean;
      /** Summarized TENSION check-in / RTI data (sent to Atlas for coaching). */
      userTensionSnapshot?: Record<string, unknown> | null;
    },
  ): Promise<AIMessage> {
    try {
      // Build context
      const context = this.buildContext(message, options);

      // Call backend API
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeadersForProtectedApi(),
        },
        body: JSON.stringify({
          message,
          context,
          config: this.config,
          conversationHistory: this.conversationHistory.slice(-this.config.contextWindow),
        }),
      });

      if (!response.ok) {
        let detail = response.statusText;
        try {
          const errJson = await response.json();
          if (errJson?.message) detail = errJson.message;
          else if (errJson?.error) detail = errJson.error;
        } catch {
          // ignore
        }
        if (response.status === 403 && String(detail).includes('PRO')) {
          throw new Error('TENSION Atlas AI is a PRO feature. Upgrade to unlock 1:1 relationship guidance.');
        }
        throw new Error(`AI request failed: ${detail}`);
      }

      const data = await response.json();
      
      // Create assistant message
      const assistantMessage: AIMessage = {
        id: this.generateId(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date().toISOString(),
        metadata: {
          tokens: data.tokens,
          model: this.config.model,
          searchResults: data.searchResults,
          codeContext: data.codeContext,
        },
      };

      // Add to conversation history
      const userMessage: AIMessage = {
        id: this.generateId(),
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
      };

      this.conversationHistory.push(userMessage, assistantMessage);

      // Keep history manageable
      if (this.conversationHistory.length > 100) {
        this.conversationHistory = this.conversationHistory.slice(-50);
      }

      return assistantMessage;
    } catch (error) {
      console.error('AI Assistant error:', error);
      throw error;
    }
  }

  /**
   * Stream message from backend with incremental deltas.
   */
  static async streamMessage(
    message: string,
    options?: {
      conversationId?: string;
      includeCodeContext?: boolean;
      includeWebSearch?: boolean;
      userTensionSnapshot?: Record<string, unknown> | null;
      aiMode?: 'atlas_pro' | 'live_companion';
      aiSurface?: 'consumer_chat' | 'atlas_live_companion';
      onDelta?: (delta: string) => void;
    },
  ): Promise<AIMessage> {
    try {
      const context = this.buildContext(message, options);
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/ai/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeadersForProtectedApi(),
        },
        body: JSON.stringify({
          message,
          context,
          config: this.config,
          conversationHistory: this.conversationHistory.slice(-this.config.contextWindow),
        }),
      });

      if (!response.ok) {
        let detail = response.statusText;
        try {
          const errJson = await response.json();
          if (errJson?.message) detail = errJson.message;
          else if (errJson?.error) detail = errJson.error;
        } catch {
          // ignore
        }
        if (response.status === 403 && String(detail).includes('PRO')) {
          throw new Error('TENSION Atlas AI is a PRO feature. Upgrade to unlock 1:1 relationship guidance.');
        }
        throw new Error(`AI request failed: ${detail}`);
      }

      if (!response.body) {
        throw new Error('AI stream unavailable (missing response body)');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const delta = decoder.decode(value, { stream: true });
        if (!delta) continue;
        fullText += delta;
        options?.onDelta?.(delta);
      }

      const assistantMessage: AIMessage = {
        id: this.generateId(),
        role: 'assistant',
        content: fullText,
        timestamp: new Date().toISOString(),
        metadata: {
          model: this.config.model,
        },
      };

      const userMessage: AIMessage = {
        id: this.generateId(),
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
      };

      this.conversationHistory.push(userMessage, assistantMessage);
      if (this.conversationHistory.length > 100) {
        this.conversationHistory = this.conversationHistory.slice(-50);
      }
      return assistantMessage;
    } catch (error) {
      console.error('AI Assistant stream error:', error);
      throw error;
    }
  }

  /**
   * Build context for AI request
   */
  private static buildContext(
    message: string,
    options?: {
      includeCodeContext?: boolean;
      includeWebSearch?: boolean;
      userTensionSnapshot?: Record<string, unknown> | null;
      aiMode?: 'atlas_pro' | 'live_companion';
      aiSurface?: 'consumer_chat' | 'atlas_live_companion';
    },
  ): any {
    const context: any = {
      codebase: null,
      searchResults: null,
    };
    if (options?.userTensionSnapshot != null) {
      context.userTensionSnapshot = options.userTensionSnapshot;
    }
    if (options?.aiMode) {
      context.aiMode = options.aiMode;
    }
    if (options?.aiSurface) {
      context.aiSurface = options.aiSurface;
    }

    // Include codebase context if enabled (explicit false from caller wins)
    const includeCode =
      options?.includeCodeContext !== undefined
        ? options.includeCodeContext
        : this.config.enableCodebaseContext;
    if (includeCode) {
      context.codebase = this.getRelevantCodeContext(message);
    }

    const includeSearch =
      options?.includeWebSearch !== undefined
        ? options.includeWebSearch
        : this.config.enableWebSearch;
    if (includeSearch) {
      context.searchQuery = this.extractSearchQuery(message);
    }

    return context;
  }

  /**
   * Get relevant code context based on message
   */
  private static getRelevantCodeContext(message: string): any {
    const messageLower = message.toLowerCase();
    const relevantFiles: any[] = [];

    // Search for relevant files based on keywords
    CODEBASE_INDEX.files.forEach(file => {
      const score = this.calculateRelevanceScore(messageLower, file);
      if (score > 0) {
        relevantFiles.push({ ...file, score });
      }
    });

    // Sort by relevance and take top 5
    const topFiles = relevantFiles
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map(f => ({
        path: f.path,
        type: f.type,
        description: f.description,
      }));

    return {
      files: topFiles,
      structure: CODEBASE_INDEX.structure,
      features: CODEBASE_INDEX.features,
      techStack: CODEBASE_INDEX.techStack,
    };
  }

  /**
   * Calculate relevance score for file
   */
  private static calculateRelevanceScore(query: string, file: any): number {
    let score = 0;

    // Check file path
    if (query.includes(file.path.toLowerCase())) score += 10;

    // Check description
    const descWords = file.description.toLowerCase().split(' ');
    descWords.forEach(word => {
      if (query.includes(word)) score += 2;
    });

    // Check exports
    file.exports.forEach((exp: string) => {
      if (query.includes(exp.toLowerCase())) score += 5;
    });

    // Check type
    if (query.includes(file.type)) score += 3;

    return score;
  }

  /**
   * Extract search query from message
   */
  private static extractSearchQuery(message: string): string | null {
    const keywords = [
      'search', 'find', 'look up', 'google', 'web',
      'latest', 'current', 'news', 'trends',
    ];

    const hasSearchIntent = keywords.some(kw => message.toLowerCase().includes(kw));
    
    if (hasSearchIntent) {
      // Extract the actual search query
      return message.replace(/^(search|find|look up|google)\s+/i, '');
    }

    return null;
  }

  /**
   * Clear conversation history
   */
  static clearHistory() {
    this.conversationHistory = [];
  }

  /**
   * Get conversation history
   */
  static getHistory(): AIMessage[] {
    return [...this.conversationHistory];
  }

  /**
   * Save conversation
   */
  static async saveConversation(title: string): Promise<string> {
    const conversation: AIConversation = {
      id: this.generateId(),
      title,
      messages: [...this.conversationHistory],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tags: this.extractTags(this.conversationHistory),
    };

    // Save to localStorage (in production, save to Supabase)
    const conversations = this.getSavedConversations();
    conversations.push(conversation);
    localStorage.setItem('tension_ai_conversations', JSON.stringify(conversations));

    return conversation.id;
  }

  /**
   * Load conversation
   */
  static loadConversation(conversationId: string): AIConversation | null {
    const conversations = this.getSavedConversations();
    return conversations.find(c => c.id === conversationId) || null;
  }

  /**
   * Get all saved conversations
   */
  static getSavedConversations(): AIConversation[] {
    const saved = localStorage.getItem('tension_ai_conversations');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error('Failed to load conversations:', error);
      }
    }
    return [];
  }

  /**
   * Delete conversation
   */
  static deleteConversation(conversationId: string) {
    const conversations = this.getSavedConversations();
    const filtered = conversations.filter(c => c.id !== conversationId);
    localStorage.setItem('tension_ai_conversations', JSON.stringify(filtered));
  }

  /**
   * Extract tags from conversation
   */
  private static extractTags(messages: AIMessage[]): string[] {
    const tags = new Set<string>();
    const content = messages.map(m => m.content.toLowerCase()).join(' ');

    // Extract common topics
    const topics = [
      'rti', 'scoring', 'check-in', 'action-card', 'timeline',
      'marketing', 'seo', 'blog', 'social-media',
      'auth', 'subscription', 'pricing', 'analytics',
      'bug', 'feature', 'code', 'debug',
    ];

    topics.forEach(topic => {
      if (content.includes(topic)) {
        tags.add(topic);
      }
    });

    return Array.from(tags);
  }

  /**
   * Generate unique ID
   */
  private static generateId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Search codebase
   */
  static searchCodebase(query: string): any[] {
    const results: any[] = [];
    const queryLower = query.toLowerCase();

    CODEBASE_INDEX.files.forEach(file => {
      const score = this.calculateRelevanceScore(queryLower, file);
      if (score > 0) {
        results.push({ ...file, score });
      }
    });

    return results.sort((a, b) => b.score - a.score);
  }

  /**
   * Get quick suggestions
   */
  static getQuickSuggestions(): string[] {
    return [
      'How does the RTI scoring algorithm work?',
      'Help me debug an issue with daily check-ins',
      'Generate a new React component for...',
      'What are the best SEO practices for the blog?',
      'How can I improve user retention?',
      'Explain the Singles Mode vs Couples Mode',
      'Show me how to create a new marketing campaign',
      'What are the current subscription tiers?',
    ];
  }

  /**
   * Get codebase statistics
   */
  static getCodebaseStats() {
    return {
      totalFiles: CODEBASE_INDEX.files.length,
      pages: CODEBASE_INDEX.structure.pages.length,
      components: CODEBASE_INDEX.structure.components.length,
      utilities: CODEBASE_INDEX.structure.utilities.length,
      features: CODEBASE_INDEX.features.length,
      techStack: CODEBASE_INDEX.techStack,
    };
  }
}

// Initialize on load
AIAssistant.loadConfig();
